/**
 * Scale — the one module that decides which answers a question's Top 2 Box and Bottom 2 Box net,
 * and what NPS bands are (M4S-5599). Spec: docs/pdlc/crosstabs-top-2-box.md, Part 1.
 *
 * Studio drew Top 2 / Bottom 2 in four places with two opposite rules, so an NPS table could show
 * `Top 2 Box (9 & 10)` and `Top 2 Box (0 & 1)` side by side. Research's rule (Lily Bollinger,
 * 2026-09-28) is by the kind of question, never by code value:
 *
 * - **Worded scale** (Excellent … Poor): Top 2 is the first two answers as programmed, Bottom 2 the
 *   last two, leaving out Don't know / N/A and similar answers after the scale.
 * - **Number scale** (1–5, or `0 - Disagree strongly` … `10 - Agree strongly`): the two highest
 *   numbers are Top 2 and the two lowest Bottom 2.
 * - **NPS** (0–10, "recommend"): never Top 2. Promoters 9–10, Neutral 7–8, Detractors 0–6.
 * - **List** (brands, states, income, ethnicity): positional, and only when a researcher switches
 *   it on for that question.
 * - **Not a scale** (fewer than four answers, open ends, ranked, collapsed, and every market
 *   question): never.
 *
 * The direction is read from both end labels, so a scale programmed negative-first (`Strongly
 * disagree` … `Strongly agree`) takes Top 2 from its positive end. A researcher's Reverse scale
 * switch (`flip`) overrides that on any question.
 *
 * **One gate.** `boxSwitches` decides which question a switch reaches: a per-question choice wins,
 * and the crosstab-wide switch reaches only `firstPass` questions, the ones Studio recognises as
 * scales by their words. There is no wider "auto" reach, because Dennis's complaint was Top 2
 * "applied broadly, including questions where it doesn't make sense".
 *
 * The module is pure and persists nothing. It is derived from the question on every compute, so it
 * cannot dirty a record or cause a POST on open. Nothing else decides eligibility, and the guard in
 * `scale.guard.test.ts` keeps a second copy of the rule from growing back.
 */

import { isGeographyQuestion } from '@/lib/crosstab/geography';
import { toMatrixGrid, type MatrixGrid } from '@/lib/crosstab/matrix-edits';
import type { CrosstabQuestionDto } from '@/lib/crosstab/question-formatter';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ScaleKind = 'worded' | 'number' | 'nps' | 'list' | 'none';

/** The `netType` a box or NPS row carries. Set by the producer, never inferred from label text. */
export type ScaleNetType =
    | 'top'
    | 'bottom'
    | 'top1'
    | 'promoters'
    | 'neutral'
    | 'detractors'
    | 'range';

/**
 * One answer with EVERY code it holds, so a later wave that re-keyed the same answer still counts.
 * Codes are exactly as the question stores them (flat `option_id`s, or a grid's choice keys), never
 * case-folded: flat comparisons go through compute's case-insensitive `hasOption`, and grid
 * comparisons are exact against `grid.choices`, which are the same strings.
 */
export interface ScaleAnswer {
    codes: string[];
    label: string;
}

export interface NpsBands {
    promoters: string[];
    neutral: string[];
    detractors: string[];
}

export interface ScaleInfo {
    kind: ScaleKind;
    /** The scale's answers, in survey order. */
    points: ScaleAnswer[];
    /** Answers after the scale (Don't know, "I'm not a beer drinker", "does not apply"). */
    leftOut: ScaleAnswer[];
    /** Codes of the two best answers, best first. Null when this question can never have a Top 2. */
    top: string[] | null;
    /** Codes of the two worst answers, worst first. Null when it can never have a Bottom 2. */
    bottom: string[] | null;
    /**
     * M4S-5777 (X79): codes of the single best answer, the first of `top` (so it follows Reverse
     * and the direction rule exactly as Top 2 does). Null wherever `top` is: NPS, not a scale.
     */
    topBox: string[] | null;
    topBoxAnswers: ScaleAnswer[];
    /** The same two answers as `top`, for the rail's "Top 2: Excellent + Very good" line. */
    topAnswers: ScaleAnswer[];
    bottomAnswers: ScaleAnswer[];
    /** Studio recognises this as a scale by its words, so the crosstab-wide switch reaches it. */
    firstPass: boolean;
    /** Top 2 comes from the end listed last, or the lowest numbers (after any flip). */
    reversed: boolean;
    /** Codes of each NPS band, mapped from the label numbers. Null unless kind is 'nps'. */
    nps: NpsBands | null;
}

/**
 * One question's choices in the Top 2 / Bottom 2 / NPS switches. Absent means "follow the
 * crosstab-wide switch". Six fields, and `PER_QUESTION_SUMMARY_KEYS` is their canonical order: the
 * record parser writes them in that order, because ai_layout is JSONB, which returns object keys
 * ordered by length, and the compute dirty check compares JSON.
 */
export interface PerQuestionSummary {
    showTopBox?: boolean;
    /** M4S-5777 (X79): the single Top Box. */
    showTop1Box?: boolean;
    showBottomBox?: boolean;
    showNPS?: boolean;
    /** Reverse scale: swaps which answers count as Top 2 and Bottom 2. */
    reversed?: boolean;
    /** Where the Top 2 / Bottom 2 rows sit (M4S-5607): an option id, or '$end' for after the answers. */
    topAbove?: string;
    bottomAbove?: string;
}

export const PER_QUESTION_SUMMARY_KEYS = [
    'showTopBox',
    'showTop1Box',
    'showBottomBox',
    'showNPS',
    'reversed',
    'topAbove',
    'bottomAbove',
] as const satisfies readonly (keyof PerQuestionSummary)[];

// ── Constants ─────────────────────────────────────────────────────────────────

/** What a box row is called. The rail, the charts and the exports all read these. */
export const BOX_NAMES = { top: 'Top 2 Box', bottom: 'Bottom 2 Box', top1: 'Top Box' } as const;

/** Juhi's workbook labels every net "(Net)", and the deck readers find answers by label substring,
 *  so a net whose label named its members would be counted twice. */
export const TOP_2_LABEL = `${BOX_NAMES.top} (Net)`;
export const BOTTOM_2_LABEL = `${BOX_NAMES.bottom} (Net)`;
/** M4S-5777 (X79): the single best answer, labelled as a net like the others. */
export const TOP_BOX_LABEL = `${BOX_NAMES.top1} (Net)`;

export const NPS_NET_LABELS = {
    promoters: 'Promoters (Net)',
    neutral: 'Neutral (Net)',
    detractors: 'Detractors (Net)',
} as const;
export const NPS_SCORE_LABEL = 'NPS Score';

/** The NPS framework: Promoters 9–10, Detractors 0–6, Neutral between (Jonathan, 2026-09-28). */
export const NPS_PROMOTER_MIN = 9;
export const NPS_DETRACTOR_MAX = 6;

/**
 * The bands as copy, for the rail and the docs: "Promoters (9–10), Neutral (7–8), Detractors (0–6)".
 * Built from the two cut points, so the words cannot drift from the numbers.
 */
export const NPS_BANDS_TEXT = `Promoters (${NPS_PROMOTER_MIN}–10), Neutral (${NPS_DETRACTOR_MAX + 1}–${NPS_PROMOTER_MIN - 1}), Detractors (0–${NPS_DETRACTOR_MAX})`;

/** A Top 2 needs a scale, and on fewer than four points Top 2 and Bottom 2 overlap. */
export const MIN_SCALE_POINTS = 4;
export const MAX_NUMBER_SCALE_RANGE = 10;

export const NPS_QIDS: readonly string[] = ['OOHNPS'];
export const NPS_TEXT = /\brecommend/i;

/** What ChartView and the PPTX chart slide plot as a percentage. NPS Score is charted as a score. */
export const CHARTED_NET_TYPES: ReadonlySet<string> = new Set<string>([
    'top',
    'bottom',
    'top1',
    'promoters',
    'neutral',
    'detractors',
    'range',
]);

/** The first word or two of the positive end of a worded scale, lower-cased. */
export const POSITIVE_END: RegExp[] = [
    /^(strongly|completely|totally) agree/,
    /^agree (strongly|completely)/,
    /^(extremely|very) likely/,
    /^definitely (will|would)(?! not)/,
    /^excellent/,
    /^(very|extremely|completely) satisfied/,
    /^i (liked|like|loved|love) (it|them)( very much| a lot)?$/,
    /^i (liked|like) (it|them) very much/,
    /^love( it| them)?$/,
    /^very good/,
    /^(very|extremely) (interested|important|appealing|favou?rable|positive|open)/,
    /^much (better|more)/,
    /^(every ?day|daily|always)$/,
    /^(i )?love (it|them)/,
    /^one of my (very )?favou?rites?/,
    /^definitely agree/,
    /^agree\/like strongly/,
    /^strongly like/,
    /^(the|one of the) best/,
    /^very strongly/,
    /^extremely\b/,
    /^very (relevant|much)/,
];

/** The negative end. */
export const NEGATIVE_END: RegExp[] = [
    /^(strongly|completely|totally) disagree/,
    /^disagree (strongly|completely)/,
    /^not at all/,
    /^(extremely|very) unlikely/,
    /^definitely (will|would) not/,
    /^(very )?poor/,
    /^(very|extremely|completely) dissatisfied/,
    /^i (disliked|dislike|hated|hate) (it|them)( very much| a lot)?/,
    /^hated?( it| them)?$/,
    /^very bad/,
    /^(very|extremely) (unfavou?rable|negative)/,
    /^much (worse|less)/,
    /^never$/,
    /^not (open|appealing)/,
    /^definitely disagree/,
    /^disagree\/dislike strongly/,
    /^strongly dislike/,
    /^not as good/,
    /^(doesn.?t|does not|did not|didn.?t)\b/,
];

/** Answers that sit after a scale without being on it. */
export const LEFT_OUT =
    /(don.?t|do not) (know|recall|remember)|not sure|unsure|^n\/?a\b|not applicable|prefer not|refuse|^none\b|never heard|not familiar|no opinion|can.?t say|cannot say|^other\b/;

/**
 * Answers that start with a negative end's catch-all words (`does not`, `did not`) but say the
 * question did not apply, so they can never be where a worded scale ends. `NEGATIVE_END` keeps the
 * catch-all for a real end such as `Did not influence at all`. Deliberately not part of
 * `LEFT_OUT`, which also trims the end of a list and reads `not familiar` anywhere in a label.
 */
export const NOT_ON_SCALE = /^(does not|doesn.?t) apply|^(did not|didn.?t) (notice|watch|see)\b/;

// ── Reading a label ───────────────────────────────────────────────────────────

/** A label without its leading number and filler, lower-cased, for matching against the word lists. */
const words = (label: string) =>
    label
        .toLowerCase()
        .replace(/<[^>]+>/g, '')
        .replace(/^\s*-?\d+(\.\d+)?\s*[-–:.)]?\s*/, '')
        .replace(/^(it|this|they) (is|was|were|did|does)\s+(?=not|very|much|the|one|extremely)/, '')
        .replace(/^(it is|it's|it’s)\s+/, '')
        .trim();

const isPos = (label: string) => {
    const w = words(label);
    return !NEGATIVE_END.some((r) => r.test(w)) && POSITIVE_END.some((r) => r.test(w));
};
const isNeg = (label: string) => NEGATIVE_END.some((r) => r.test(words(label)));

/** The integer a label starts with (`10 - Agree strongly`, `7`), or null. `9-10` and `Top 2 Box (9 & 10)` are not one. */
const leadingInt = (label: string): number | null => {
    const m = label.match(/^\s*(-?\d+)(?:\s*$|\s*[-–:.)]\s*\D|\s+\D)/);
    return m ? parseInt(m[1], 10) : null;
};

const codesOf = (answers: ScaleAnswer[]) => answers.flatMap((a) => a.codes);

/**
 * Which NPS band a label's leading number falls in, against the two cut points. Null for a label
 * that does not start with a number from 0 to 10, such as `Top 2 Box (9 & 10)` or `Promoters (Net)`.
 * The decks' NPS fallbacks read their bands through this, so the cut points live here only.
 */
export function npsBandOfLabel(label: string): keyof NpsBands | null {
    const n = leadingInt(label);
    if (n === null || n < 0 || n > 10) return null;
    if (n >= NPS_PROMOTER_MIN) return 'promoters';
    if (n <= NPS_DETRACTOR_MAX) return 'detractors';
    return 'neutral';
}

// ── Classification ────────────────────────────────────────────────────────────

export interface ScaleInput {
    qid: string;
    /** The column the question's responses are read from, when a wave variant carries a suffixed qid. */
    responseColumnId?: string;
    text: string;
    questionType: string;
    /** In survey order. */
    answers: ScaleAnswer[];
}

/**
 * Classifies one question and picks its Top 2 / Bottom 2 answers. Rules, in order:
 *
 * 1. Not a scale: a market question (DMA and STATE, whose Nielsen codes look like a scale), an open
 *    end, ranked or collapsed question, or fewer than four answers.
 * 2. Number scale: every answer up to the last numbered one starts with an integer, the integers
 *    are consecutive, there are at least four, and max − min ≤ 10. Everything after the last
 *    numbered answer is left out. Top 2 is the two highest numbers, and swaps only when the low end
 *    reads positive and the high end negative. `firstPass` needs scale words on at least one end.
 * 3. NPS: a number scale running exactly 0–10 whose QID is an NPS QID or whose text says recommend.
 * 4. Worded scale: the first answer matches one end's word list, and the scale runs to the last
 *    answer matching the opposite end, not counting a `NOT_ON_SCALE` answer such as `Does not
 *    apply`. Anything after that is left out. Top 2 is the first two answers, or the last two when
 *    the first is the negative end. Always `firstPass`.
 * 5. List: anything else with at least four answers after a trailing run of left-out answers.
 *    Positional, and never `firstPass`.
 *
 * `flip` swaps Top and Bottom on worded, number and list questions, and does nothing on NPS or
 * on a question that is not a scale.
 */
export function classifyScale(input: ScaleInput, opts: { flip?: boolean } = {}): ScaleInfo {
    const none = (points: ScaleAnswer[] = [], leftOut: ScaleAnswer[] = []): ScaleInfo => ({
        kind: 'none',
        points,
        leftOut,
        top: null,
        bottom: null,
        topBox: null,
        topBoxAnswers: [],
        topAnswers: [],
        bottomAnswers: [],
        firstPass: false,
        reversed: false,
        nps: null,
    });

    if (isGeographyQuestion(input)) return none(input.answers);
    // M4S-5777 (X63): an allocation's items are amounts shared out, never a scale.
    if (/open_end|ranked|collapsed|allocation/.test(input.questionType)) return none(input.answers);
    const answers = input.answers;
    if (answers.length < MIN_SCALE_POINTS) return none(answers);

    /** `bestFirst` is the scale's answers ordered best to worst. */
    const boxes = (
        kind: 'worded' | 'number' | 'list',
        points: ScaleAnswer[],
        leftOut: ScaleAnswer[],
        bestFirst: ScaleAnswer[],
        firstPass: boolean,
        autoReversed: boolean,
    ): ScaleInfo => {
        let top = bestFirst.slice(0, 2);
        let bottom = bestFirst.slice(-2).reverse();
        if (opts.flip) [top, bottom] = [bottom, top];
        return {
            kind,
            points,
            leftOut,
            top: codesOf(top),
            bottom: codesOf(bottom),
            topBox: codesOf(top.slice(0, 1)),
            topBoxAnswers: top.slice(0, 1),
            topAnswers: top,
            bottomAnswers: bottom,
            firstPass,
            reversed: autoReversed !== !!opts.flip,
            nps: null,
        };
    };

    // Number scale.
    const ints = answers.map((a) => leadingInt(a.label));
    let last = -1;
    ints.forEach((v, i) => {
        if (v !== null) last = i;
    });
    const numbered = answers.slice(0, last + 1);
    if (numbered.length >= MIN_SCALE_POINTS && ints.slice(0, last + 1).every((v) => v !== null)) {
        const vals = ints.slice(0, last + 1) as number[];
        const sorted = [...vals].sort((a, b) => a - b);
        const consecutive = sorted.every((v, i) => i === 0 || v === sorted[i - 1] + 1);
        if (consecutive && sorted[sorted.length - 1] - sorted[0] <= MAX_NUMBER_SCALE_RANGE) {
            const byValue = numbered.map((a, i) => ({ a, v: vals[i] })).sort((x, y) => x.v - y.v);
            const lowLabel = byValue[0].a.label;
            const highLabel = byValue[byValue.length - 1].a.label;
            const lowIsTop = isPos(lowLabel) && isNeg(highLabel);
            const recognised =
                isPos(lowLabel) || isNeg(lowLabel) || isPos(highLabel) || isNeg(highLabel);
            const leftOut = answers.slice(last + 1);

            const isNps =
                sorted[0] === 0 &&
                sorted[sorted.length - 1] === 10 &&
                !lowIsTop &&
                (NPS_QIDS.includes((input.responseColumnId ?? input.qid).toUpperCase()) ||
                    NPS_TEXT.test(input.text));
            if (isNps) {
                const band = (inBand: (v: number) => boolean) =>
                    byValue.filter((x) => inBand(x.v)).flatMap((x) => x.a.codes);
                return {
                    ...none(numbered, leftOut),
                    kind: 'nps',
                    nps: {
                        promoters: band((v) => v >= NPS_PROMOTER_MIN),
                        neutral: band((v) => v > NPS_DETRACTOR_MAX && v < NPS_PROMOTER_MIN),
                        detractors: band((v) => v <= NPS_DETRACTOR_MAX),
                    },
                };
            }

            const bestFirst = (lowIsTop ? byValue : [...byValue].reverse()).map((x) => x.a);
            return boxes('number', numbered, leftOut, bestFirst, recognised, lowIsTop);
        }
    }

    // Worded scale: both ends recognised, and the scale runs to the opposite end.
    const first = answers[0].label;
    const startsPositive = isPos(first);
    const startsNegative = !startsPositive && isNeg(first);
    if (startsPositive || startsNegative) {
        for (let i = answers.length - 1; i >= MIN_SCALE_POINTS - 1; i--) {
            // `Does not apply` is not the scale's worst point, however its words read.
            if (NOT_ON_SCALE.test(words(answers[i].label))) continue;
            const opposite = startsPositive ? isNeg(answers[i].label) : isPos(answers[i].label);
            if (!opposite) continue;
            const points = answers.slice(0, i + 1);
            const bestFirst = startsNegative ? [...points].reverse() : points;
            return boxes('worded', points, answers.slice(i + 1), bestFirst, true, startsNegative);
        }
    }

    // List: positional, and never automatic.
    let end = answers.length;
    while (end > 0 && LEFT_OUT.test(words(answers[end - 1].label))) end--;
    const points = answers.slice(0, end);
    if (points.length < MIN_SCALE_POINTS) return none(points, answers.slice(end));
    return boxes('list', points, answers.slice(end), points, false, false);
}

/**
 * M4S-5777 (decision C3). An answer that is on no order: Don't know, N/A, None of these, Other,
 * Prefer not to say, Does not apply. Read with the same words as the scale rule, so the order
 * comparisons in `conditions.ts` leave out exactly the answers Top 2 leaves out.
 */
export function isOffScaleLabel(label: string): boolean {
    const w = words(label);
    return LEFT_OUT.test(w) || NOT_ON_SCALE.test(w);
}

/**
 * A question's answers as scale points: every flat option that is neither a `scale-A-B` id nor an
 * open-end net (all of its codes), or a matrix's answer choices in survey order. Membership follows
 * the question as surveyed, never display edits (hide, reorder, rename, merge).
 */
export function answersOf(q: CrosstabQuestionDto): ScaleAnswer[] {
    const grid = toMatrixGrid(q);
    if (grid) return grid.choices.map((c) => ({ codes: [c.key], label: c.label }));
    return q.options
        .filter((o) => !o.option_id.some(isScaleNetOptionId) && !(q.isOpenEnd && o.isNet))
        .map((o) => ({ codes: [...o.option_id], label: o.option }));
}

export function scaleOf(q: CrosstabQuestionDto, opts: { flip?: boolean } = {}): ScaleInfo {
    return classifyScale(
        {
            qid: q.qid,
            responseColumnId: q.responseColumnId,
            text: q.question,
            questionType: q.questionType,
            answers: answersOf(q),
        },
        opts,
    );
}

// ── The gate ──────────────────────────────────────────────────────────────────

/** The crosstab-wide switches `boxSwitches` reads. `ComputeOpts` satisfies it. */
export interface BoxSwitchOpts {
    showTopBox?: boolean;
    showTop1Box?: boolean;
    showBottomBox?: boolean;
    showNPS?: boolean;
}

/**
 * A question's entry in a per-question map, which is keyed by upper-cased qid (box-rows.ts writes
 * it that way). Falls back to the qid as stored, for a map written before that rule.
 */
export function boxEntryOf(
    map: Readonly<Record<string, PerQuestionSummary>> | undefined,
    qid: string,
): PerQuestionSummary | undefined {
    return map?.[qid.toUpperCase()] ?? map?.[qid];
}

/**
 * Which box rows a question draws. The only gate: compute, the rail and the summary tables all
 * call it, so what the rail says Studio picks is exactly what the switch draws.
 *
 * - A per-question choice wins over the crosstab-wide switch, and the wide switch reaches only
 *   `firstPass` questions. A list is drawn only when switched on for that question.
 * - Nothing is drawn where the question can have no Top 2 (`top` null): NPS, and anything that is
 *   not a scale, whatever the switches say.
 * - NPS nets are drawn on kind 'nps' only, by the per-question choice or else the NPS switch.
 * - `flip` is the researcher's Reverse scale. Pass it to `scaleOf` before calling this.
 */
export function boxSwitches(
    info: ScaleInfo,
    entry: PerQuestionSummary | undefined,
    opts: BoxSwitchOpts,
): { showTop: boolean; showTop1: boolean; showBottom: boolean; showNps: boolean; flip: boolean } {
    const showTop =
        info.top !== null && (entry?.showTopBox ?? (!!opts.showTopBox && info.firstPass));
    // X79: the single Top Box, by the same gate: the question's own choice, else the crosstab-wide
    // switch on the questions Studio picks.
    const showTop1 =
        info.topBox !== null && (entry?.showTop1Box ?? (!!opts.showTop1Box && info.firstPass));
    const showBottom =
        info.bottom !== null && (entry?.showBottomBox ?? (!!opts.showBottomBox && info.firstPass));
    const showNps = info.kind === 'nps' && (entry?.showNPS ?? !!opts.showNPS);
    return { showTop, showTop1, showBottom, showNps, flip: entry?.reversed === true };
}

// ── Matrices ──────────────────────────────────────────────────────────────────

/**
 * The tokens a statement holds for a set of answer choices: the union of `grid.cells[statement]`
 * over the choices whose key is one of `codes`, in survey order, without repeats. The key match is
 * exact, because a derived grid keys its choices by label (`Love it`), and a stored grid's cells
 * already hold every wave's tokens for a merged choice. Empty when the statement has none.
 */
export function statementBoxTokens(
    grid: MatrixGrid,
    statement: number,
    codes: readonly string[],
): string[] {
    const wanted = new Set(codes);
    const tokens: string[] = [];
    grid.choices.forEach((choice, c) => {
        if (!wanted.has(choice.key)) return;
        for (const token of grid.cells[statement]?.[c] ?? []) {
            if (!tokens.includes(token)) tokens.push(token);
        }
    });
    return tokens;
}

// ── Retired options ───────────────────────────────────────────────────────────

/** A synthetic net option id such as `scale-4-5`, not an actual response code. */
export function isScaleNetOptionId(id: string): boolean {
    return /^scale-\d+-\d+$/i.test(id);
}

/**
 * True for a `scale-A-B` option id that the mapper used to draw as an always-on Top 2 / Bottom 2
 * on a 0–10 slider and no longer emits: any such id on a question whose range is 10 or less, or
 * unknown. Compute drops these on the row path of every question, NPS included, so a fixture or a
 * browser override saved before the mapper changed does not draw a second, contradicting pair.
 * Buckets above 10 (`0 - 9` …) are real ranges and stay, as netType 'range'.
 */
export function isRetiredBoxOptionId(q: { max?: number | null }, id: string): boolean {
    return isScaleNetOptionId(id) && (q.max == null || q.max <= 10);
}

// ── Mean (M4S-5777, X78) ──────────────────────────────────────────────────────

/**
 * How a question's answers are averaged: the scale's ends and the value of each code that counts.
 * `valueOf` is null for a code that is not on the scale (Don't know, N/A, an out-of-range code), so
 * it is left out of the Mean and the Median, never averaged in.
 */
export interface MeanRule {
    min: number;
    max: number;
    valueOf: (code: string) => number | null;
}

/** The question types whose answers are numbers, with a stored min and max. */
const NUMBER_QUESTION_TYPES = /^(intensity_scale|radio intensity scale)$/;

const NUMERIC_CODE = /^-?\d+(\.\d+)?$/;

/**
 * Whether a question gets a Mean and Median, and how its answers are valued (M4S-5777, X78; PRD
 * decision 2 and design decision S2). Spec: docs/pdlc/crosstabs-significance-full.md §5.
 *
 * Decided by what the question is, never by the shape of its codes:
 *
 * - **A number question** (an intensity scale or slider with a stored min and max): each code is
 *   its number, and only a number from min to max counts. A Don't know coded 98 on a 0–10 scale,
 *   or any answer whose words say it is off the scale, is left out.
 * - **A number scale or NPS** (`scaleOf` kind `number` / `nps`): each answer is the number its
 *   label starts with; the answers after the scale (`leftOut`) are left out.
 * - **A worded scale** (`worded`): each answer is valued by its code as programmed (decision S2),
 *   the scale's own answers only, and only when every one of them has a whole-number code.
 * - **Never**: a list (Gender, brands, income, even one switched on for Top 2), a market question,
 *   an open end, a ranked or collapsed question, a matrix (its Mean is the matrix producer's), and
 *   any question a respondent answered with more than one code (`multiAnswer`), because several
 *   codes are not one value.
 *
 * Pure, persisted nowhere, and the only rule: compute draws a Mean row where this returns one.
 */
export function meanRuleOf(
    q: CrosstabQuestionDto,
    opts: { multiAnswer?: boolean } = {},
): MeanRule | null {
    if (opts.multiAnswer) return null;
    if (isGeographyQuestion(q)) return null;
    if (toMatrixGrid(q)) return null;

    if (NUMBER_QUESTION_TYPES.test(q.questionType) && q.min != null && q.max != null) {
        const min = Math.min(q.min, q.max);
        const max = Math.max(q.min, q.max);
        const offScale = new Set(
            q.options
                .filter((o) => LEFT_OUT.test(words(o.option)) || NOT_ON_SCALE.test(words(o.option)))
                .flatMap((o) => o.option_id.map((id) => id.toUpperCase())),
        );
        return {
            min,
            max,
            valueOf: (code) => {
                if (offScale.has(code.toUpperCase()) || !NUMERIC_CODE.test(code)) return null;
                const v = Number(code);
                return v >= min && v <= max ? v : null;
            },
        };
    }

    const info = scaleOf(q);
    const valued: Array<{ codes: string[]; v: number }> = [];
    if (info.kind === 'number' || info.kind === 'nps') {
        for (const a of info.points) {
            const v = leadingInt(a.label);
            if (v === null) return null;
            valued.push({ codes: a.codes, v });
        }
    } else if (info.kind === 'worded') {
        for (const a of info.points) {
            const first = a.codes[0];
            if (first === undefined || !/^-?\d+$/.test(first)) return null;
            valued.push({ codes: a.codes, v: parseInt(first, 10) });
        }
    } else {
        return null;
    }
    if (valued.length < 2) return null;
    const byCode = new Map<string, number>();
    for (const { codes, v } of valued) for (const c of codes) byCode.set(c.toUpperCase(), v);
    const values = valued.map((x) => x.v);
    return {
        min: Math.min(...values),
        max: Math.max(...values),
        valueOf: (code) => byCode.get(code.toUpperCase()) ?? null,
    };
}

// ── A matrix statement's Mean (M4S-5777, X60) ─────────────────────────────────

/** A statement's Mean rule: each cell token's value on the scale, and the scale's ends. */
export interface MatrixMeanRule {
    valueById: Map<string, number>;
    min: number;
    max: number;
}

/**
 * How statement `s` of a matrix is averaged, or null when its scale gives no Mean (M4S-5777, X60;
 * decision S2). The one rule for a matrix's Mean: the intensity-scale statement tables and every
 * Mean summary read it.
 *
 * - **An intensity scale matrix**: by the value of each scale point the statement has, when it has
 *   at least `MIN_SCALE_POINTS` and every one is a number (as its tables have always read it).
 * - **A standard matrix whose answer choices are a scale** (`scaleOf` kind `worded`, `number` or
 *   `nps`): a worded scale by each answer choice's code as programmed, and only when every scale
 *   point's code is a whole number; a number scale and NPS by the number each label starts with.
 *   The answers after the scale (Don't know, N/A) are left out. Its ends are the scale's, whichever
 *   points the statement holds.
 * - **Never** a list of answer choices (brands), and never by the shape of a code alone.
 *
 * Pure. Cell tokens are the grid's (`grid.cells[s][c]`), upper-cased.
 */
export function matrixMeanRule(
    q: CrosstabQuestionDto,
    grid: MatrixGrid,
    s: number,
): MatrixMeanRule | null {
    const cell = (c: number) => grid.cells[s]?.[c] ?? [];
    const valued: Array<{ c: number; v: number }> = [];
    if (!grid.choiceKeyed) {
        grid.choices.forEach((choice, c) => {
            if (cell(c).length) valued.push({ c, v: parseFloat(choice.label) });
        });
        if (valued.length < MIN_SCALE_POINTS || valued.some(({ v }) => isNaN(v))) return null;
    } else {
        const info = scaleOf(q);
        if (info.kind !== 'worded' && info.kind !== 'number' && info.kind !== 'nps') return null;
        const indexOfCode = new Map(grid.choices.map((choice, c) => [choice.key, c]));
        for (const point of info.points) {
            const code = point.codes[0];
            const c = code === undefined ? undefined : indexOfCode.get(code);
            if (c === undefined) return null;
            const v =
                info.kind === 'worded'
                    ? /^-?\d+$/.test(code)
                        ? parseInt(code, 10)
                        : null
                    : leadingInt(point.label);
            if (v === null) return null;
            valued.push({ c, v });
        }
        if (valued.length < 2 || !valued.some(({ c }) => cell(c).length)) return null;
    }
    const valueById = new Map<string, number>();
    for (const { c, v } of valued) for (const id of cell(c)) valueById.set(id, v);
    const values = valued.map(({ v }) => v);
    return { valueById, min: Math.min(...values), max: Math.max(...values) };
}
